import { FC, useRef, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { Field, Button, Icon } from '@eon-home/react-library';

import {
    WallBoxUpdateDeviceRequestModel,
    WallBoxAddDeviceRangeModelUnitEnum,
    WallBoxUpdateElectricalVehicleRequestModel,
    WallBoxAddElectricalVehicleDeviceRangeModelUnitEnum,
} from '@swagger-http';

import { Scope } from '@tools/enums';
import { analytics } from '@tools/analytics';
import { WithError } from '@hocs/with-error';
import { WithLoader } from '@hocs/with-loader';
import { WithScopes } from '@hocs/with-scopes';
import { CLEANUP_TIME } from '@tools/constants';
import { NumberOrNull } from '@tools/types';
import { setGridXStationData, setStationData } from '@store/actions';
import {
    useAppSelector,
    useGetProvider,
    useIsGridXCustomer,
    useWallboxConfigId,
} from '@store/selectors';
import {
    clearTimer,
    createTimer,
    decorateText,
    useRangeUnit,
    composeClassName,
    hasErrors,
} from '@tools/utils';
import InfoIcon from '@assets/icons/emobility/info.svg';

import './index.scss';
import { EmobilityVendors } from '@store/enums';

interface Props {
    title?: string;
    text?: string;
    centerText?: boolean;
    buttonText?: string;
    onSuccess?: () => void;
    allowSkip?: boolean;
    isGridxFlow?: boolean;
    isManualConnectCar?: boolean;
    isWallboxManagement?: boolean;
}

const MIN_ENERGY_CONSUMPTION = 1;

export const EmobilitySettings: FC<Readonly<Props>> = ({
    text,
    title,
    onSuccess,
    buttonText,
    isGridxFlow = false,
    isManualConnectCar = false,
    centerText = false,
    allowSkip = false,
    isWallboxManagement = false,
}: Props) => {
    const timeout = useRef<NumberOrNull>(null);

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        register,
        setValue,
        handleSubmit,
        formState: { errors, isDirty },
    } = useForm({
        mode: 'onBlur',
    });

    const wallbox = useAppSelector(
        (state) => state.emobility.wallboxData.wallbox,
    );
    const electricCar = useAppSelector(
        (state) => state.emobility.electricCarData.electricCar,
    );

    const isGridX = useIsGridXCustomer();
    const rangeUnit = useRangeUnit();
    const wallboxConfigId = useWallboxConfigId();
    const isEnodeProviderPaired = !!useGetProvider(EmobilityVendors.ENODE)
        ?.paired;

    const [error, setError] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const saveButtonOnClick = useCallback(() => {
        if (!isDirty) return;

        return allowSkip
            ? analytics.settingsEvSave()
            : analytics.settingsEvChange();
    }, [allowSkip, isDirty]);

    useDeepCompareEffect(() => {
        const wallboxVehicle = wallbox?.deviceConfiguration?.vehicles?.[0];
        const electricCarInfo = electricCar?.deviceConfiguration?.information;

        // Using energy consumption(rangeCapacity) from wallbox because we don't get it from enode
        const rangeCapacity = wallboxVehicle?.rangeCapacity?.value ?? null;

        const getVehicleInfo = () => {
            if (isEnodeProviderPaired && electricCarInfo) {
                const { range, batteryCapacity, brand, model } =
                    electricCarInfo;

                return {
                    range: range ?? null,
                    batteryCapacity: batteryCapacity ?? null,
                    vehicleType: `${brand ?? ''} ${model ?? ''}`.trim(),
                };
            }

            if (!isEnodeProviderPaired && wallboxVehicle) {
                const { range, batteryCapacity, vehicleType } = wallboxVehicle;

                return {
                    range: range?.value ?? null,
                    batteryCapacity: batteryCapacity ?? null,
                    vehicleType: vehicleType ?? null,
                };
            }

            return null;
        };

        const vehicle = getVehicleInfo();

        if (!vehicle) return;

        const { range, batteryCapacity, vehicleType } = vehicle;

        setValue('range', range);
        setValue('carType', vehicleType);
        setValue('rangeCapacity', rangeCapacity);
        setValue('batteryCapacity', batteryCapacity);
    }, [wallbox, setValue, electricCar, isEnodeProviderPaired]);

    useEffect(() => {
        if (error) {
            timeout.current = createTimer('timeout', CLEANUP_TIME, () => {
                setError(false);
            });
        }

        return () => {
            clearTimer(timeout.current);
        };
    }, [error]);

    const onSubmit = async ({
        range,
        carType,
        rangeCapacity,
        batteryCapacity,
    }: {
        range: number;
        carType: string;
        rangeCapacity: number;
        batteryCapacity: number;
    }) => {
        if (!wallboxConfigId) {
            setError(true);
            return;
        }

        setIsSubmitting(true);

        const payload:
            | WallBoxUpdateDeviceRequestModel
            | WallBoxUpdateElectricalVehicleRequestModel = {
            range: {
                value: range || 0,
                unit:
                    rangeUnit === 'mi'
                        ? WallBoxAddDeviceRangeModelUnitEnum.Mile
                        : WallBoxAddDeviceRangeModelUnitEnum.Km,
            },
            carType: carType || '',
            rangeCapacity: rangeCapacity || 0,
            batteryCapacity: batteryCapacity || 0,
        };

        const updateAction = () =>
            isGridX
                ? setGridXStationData(wallboxConfigId, {
                      ...payload,
                      range: {
                          ...payload.range,
                          unit:
                              rangeUnit === 'mi'
                                  ? WallBoxAddElectricalVehicleDeviceRangeModelUnitEnum.Mile
                                  : WallBoxAddElectricalVehicleDeviceRangeModelUnitEnum.Km,
                      },
                  } as WallBoxUpdateElectricalVehicleRequestModel)
                : setStationData(wallboxConfigId, payload);

        const result = await dispatch(updateAction());

        if (typeof onSuccess === 'function' && result) {
            onSuccess();
        }

        timeout.current = createTimer('timeout', CLEANUP_TIME / 3, () => {
            setIsSubmitting(false);

            if (!result) {
                setError(true);
            }
        });
    };

    const getValidationProps = (max: number) => ({
        min: {
            value: 1,
            message: t('Please use a whole number between 1 and {{max}}', {
                max,
            }),
        },
        max: {
            value: max,
            message: t('Please use a whole number between 1 and {{max}}', {
                max,
            }),
        },
        validate: (input: string) =>
            Number.isInteger(Number(input)) ||
            (t('Please use a whole number.') as string),
    });

    return (
        <div
            className={composeClassName(
                'c-emobility-settings',
                [],
                [centerText ? 'text-center' : ''],
            )}
        >
            <WithError error={!wallboxConfigId} errorProps={{ centerText }}>
                <header className="c-emobility-settings__head">
                    {title && <h2>{title}</h2>}

                    {text && (
                        <p
                            dangerouslySetInnerHTML={{
                                __html: decorateText(text),
                            }}
                        />
                    )}
                </header>
                {isWallboxManagement && (
                    <>
                        <h2>{t('EV details')}</h2>
                        {isEnodeProviderPaired && (
                            <div className="c-emobility-settings__info">
                                <Icon src={InfoIcon} />
                                {t(
                                    'Your electric vehicle information have been updated based on your connected vehicle.',
                                )}
                            </div>
                        )}
                    </>
                )}
                {!isEnodeProviderPaired && (
                    <form
                        className="c-emobility-settings__form"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <div className="c-emobility-settings__form-body">
                            <Field
                                label={t('Car make/model')}
                                placeholder={t('Car make/model')}
                                labelHint={t('(e.g. VW ID3)')}
                                register={register('carType')}
                                name="carType"
                                error={errors.carType}
                                type="text"
                                disabled={isEnodeProviderPaired}
                            />
                            <Field
                                label={t('Range')}
                                register={register('range', {
                                    ...getValidationProps(1000),
                                    required: isGridX
                                        ? (t('Range is required') as string)
                                        : undefined,
                                })}
                                name="range"
                                error={errors.range}
                                placeholder={t('Specify in {{rangeUnit}}', {
                                    rangeUnit,
                                })}
                                type="number"
                                hint={rangeUnit as unknown as string}
                                disabled={isEnodeProviderPaired}
                            />
                            <Field
                                label={t('Battery capacity')}
                                register={register('batteryCapacity', {
                                    ...getValidationProps(150),
                                    required: isGridX
                                        ? t('Battery capacity is required')
                                        : undefined,
                                })}
                                name="batteryCapacity"
                                error={errors.batteryCapacity}
                                placeholder={t('Specify in kWh')}
                                type="number"
                                hint="kWh"
                                disabled={isEnodeProviderPaired}
                            />
                            <Field
                                label={t('Energy consumption')}
                                register={register('rangeCapacity', {
                                    required: isGridX
                                        ? t('Energy consumption is required')
                                        : undefined,
                                    min: {
                                        value: MIN_ENERGY_CONSUMPTION,
                                        message: t(
                                            'The minimum allowed value is {{ min }}',
                                            {
                                                min: MIN_ENERGY_CONSUMPTION,
                                            },
                                        ),
                                    },
                                })}
                                name="rangeCapacity"
                                error={errors.rangeCapacity}
                                placeholder={t('Specify per 100{{rangeUnit}}', {
                                    rangeUnit,
                                })}
                                type="number"
                                hint={`kWh/100${rangeUnit}`}
                            />
                        </div>
                        <div>
                            {isGridxFlow && (
                                <div className="c-gridx-section__body c-gridx-section__body--paragraph c-smart-charging__settings-manual-car--paragraph">
                                    <p>
                                        {t(
                                            'You can change this anytime under "More" in the EV settings.',
                                        )}
                                    </p>
                                </div>
                            )}
                        </div>
                        <footer className="c-emobility-settings__form-actions">
                            {!isGridxFlow && (
                                <p>
                                    {t(
                                        'We will only use this information according to your privacy policy.',
                                    )}
                                </p>
                            )}

                            <WithLoader
                                loading={isSubmitting}
                                loaderProps={{ small: true }}
                            >
                                <div
                                    className={composeClassName(
                                        'c-emobility-settings__form-buttons',
                                        [isGridxFlow ? 'center' : ''],
                                    )}
                                >
                                    {typeof onSuccess === 'function' &&
                                        allowSkip && (
                                            <Button
                                                className={composeClassName(
                                                    'c-btn',
                                                    [
                                                        isManualConnectCar
                                                            ? 'outline'
                                                            : 'turquoise',
                                                    ],
                                                )}
                                                type="button"
                                                onClick={() => onSuccess()}
                                            >
                                                {t('Skip')}
                                            </Button>
                                        )}

                                    <Button
                                        type="submit"
                                        onClick={saveButtonOnClick}
                                        className={
                                            !isDirty ? 'is-disabled' : undefined
                                        }
                                        disabled={hasErrors(errors)}
                                    >
                                        {buttonText ?? t('Save')}
                                    </Button>
                                </div>
                            </WithLoader>

                            {error && (
                                <p className="c-emobility-settings__form-message">
                                    {t(
                                        'Something went wrong. Please try again later.',
                                    )}
                                </p>
                            )}
                        </footer>
                    </form>
                )}
            </WithError>
        </div>
    );
};

export default (props: Props) => (
    <WithScopes requiredScopes={[Scope.ENERGYDEVICES_EV_WRITE]}>
        <EmobilitySettings {...props} />
    </WithScopes>
);
